import { ChevronLeft } from '@mui/icons-material';
import Wallet from '@mui/icons-material/AccountBalanceWalletOutlined';
import Add from '@mui/icons-material/AddCircleOutlineOutlined';
import AppsOutlined from '@mui/icons-material/AppsOutlined';
import Support from '@mui/icons-material/ContactSupportOutlined';
import Pickups from '@mui/icons-material/DeliveryDiningOutlined';
import ExtensionOutlined from '@mui/icons-material/ExtensionOutlined';
import Reports from '@mui/icons-material/FolderCopyOutlined';
import MyUsers from '@mui/icons-material/GroupsOutlined';
import HistoryIcon from '@mui/icons-material/HistoryOutlined';
import BatchesIcon from '@mui/icons-material/LayersOutlined';
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined';
import Management from '@mui/icons-material/ManageAccountsOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Rates from '@mui/icons-material/PaidOutlined';
import AboutP99 from '@mui/icons-material/PlaceOutlined';
import Coverage from '@mui/icons-material/PriceChangeOutlined';
import Invoices from '@mui/icons-material/ReceiptLongOutlined';
import PointLocation from '@mui/icons-material/RouteOutlined';
import Dashboard from '@mui/icons-material/StackedBarChartOutlined';
import P99 from '@mui/icons-material/StorefrontOutlined';
import Resources from '@mui/icons-material/TipsAndUpdatesOutlined';
import Shipments from '@mui/icons-material/UnarchiveOutlined';
import WarehouseOutlined from '@mui/icons-material/WarehouseOutlined';
import Integrations from '@mui/icons-material/WebhookOutlined';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router';

import FulfillIcon from '@/assets/sidebar/fulfill';
import InternationalIcon from '@/assets/sidebar/international';
import Inventory from '@/assets/sidebar/inventory';
import InventoryHistory from '@/assets/sidebar/inventoryHistory';
import OrderStatusFF from '@/assets/sidebar/orderStatusFF';
import ShipmentsIcon from '@/assets/sidebar/shipments';
import Button from '@/components/Button';
import { VITE_MASSIVE_LOAD_URL } from '@/constants/env';
import { isFalabellaEnabledCountry } from '@/constants/falabella';
import { INTERNATIONAL_SECTION_ENABLED, SIDEBAR_PATHS } from '@/constants/paths';
import { useEnvioFlexAccess } from '@/hooks/useEnvioFlexAccess';
import { useFalabellaAccess } from '@/hooks/useFalabellaAccess';
import { useFulfillAccess } from '@/hooks/useFulfillAccess';
import { useMLFulfillAccess } from '@/hooks/useMLFulfillAccess';
import { useMLParcelAccess } from '@/hooks/useMLParcelAccess';
import { RootState } from '@/redux/store';
import { formatCurrency } from '@/utils/common';

import { CollapsableItem } from './CollapsableItem';
import { ListItemButtonChildren, ListItemTextCustom } from './dependencies';

interface IDrawerMenuProps {
  openDrawer: boolean;
  toggleDrawer: () => void;
  isSmallScreen?: boolean;
  closeDrawerOnMobile?: () => void;
  toggleBalanceDrawer?: () => void;
}

const ExternalLinkIcon = ({ openDrawer }: { openDrawer: boolean }) => {
  const theme = useTheme();
  return (
    <OpenInNewIcon
      sx={{
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: theme.palette.grey[500],
        opacity: openDrawer ? 1 : 0,
        transition: 'opacity 0.2s',
        marginLeft: 'auto',
      }}
    />
  );
};

const DrawerMenu = ({
  toggleDrawer,
  openDrawer,
  isSmallScreen,
  closeDrawerOnMobile,
  toggleBalanceDrawer,
}: IDrawerMenuProps) => {
  const { profile, balance } = useSelector((state: RootState) => state.parcel);
  const country = useMemo(() => profile.data?.country, [profile]);
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const availableRates = ['MEX', 'CHL'];
  const availableCoverage = ['MEX', 'COL'];
  const { hasFulfillAccess } = useFulfillAccess();
  const { hasMLParcelAccess } = useMLParcelAccess();
  const { hasEnvioFlexAccess } = useEnvioFlexAccess();
  const { hasMLFulfillAccess } = useMLFulfillAccess();
  const { hasFalabellaAccess } = useFalabellaAccess();
  const role = profile.data?.configuration.role;
  const isActive = (path: string) => location.pathname === path;

  return (
    <Box
      sx={{
        flex: 1,
        flexGrow: 1,
        overflowY: 'auto',
        position: 'relative',
        paddingTop: '64px',
        height: 'calc(100vh)',
      }}
    >
      {!isSmallScreen && (
        <IconButton
          onClick={toggleDrawer}
          sx={{
            position: 'fixed',
            top: '100px',
            left: openDrawer ? '250px' : '47px',
            zIndex: 1500,
            transform: openDrawer ? 'none' : 'rotate(180deg)',
            transition: '0.215s',
            bgcolor: '#fff',
            border: '1px solid #e0e0e0',
            width: '20px',
            height: '20px',
            ':hover': {
              bgcolor: theme.palette.primary.main,
              color: 'white',
            },
          }}
        >
          <ChevronLeft />
        </IconButton>
      )}
      <List sx={{ padding: '1rem 0' }} component="div">
        {isSmallScreen && (
          <Box
            sx={{
              display: 'flex',
              '@media (min-width: 600px)': {
                display: 'none',
              },
              flexDirection: 'column',
              gap: '1rem',
              padding: '0 1rem',
              mb: '1rem',
            }}
          >
            {profile.data?.configuration.paymentType === 'PREPAID' && (
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                bgcolor="#f6f6fb"
                padding="0.75rem 1rem"
                borderRadius="12px"
              >
                <Box display="flex" alignItems="center" gap="0.5rem">
                  <Wallet sx={{ color: theme.palette.grey[500] }} fontSize="medium" />
                  <Box display="flex" flexDirection="column">
                    <span style={{ fontSize: '0.75rem', color: theme.palette.grey[600] }}>
                      Mi Saldo
                    </span>
                    {balance.loading ? (
                      <CircularProgress size={16} color="primary" />
                    ) : (
                      <strong style={{ fontSize: '1.1rem', color: theme.palette.secondary.main }}>
                        {formatCurrency(balance.data, profile.data?.country)}
                      </strong>
                    )}
                  </Box>
                </Box>
                <IconButton
                  sx={{
                    bgcolor: theme.palette.primary.main,
                    color: 'white',
                    ':hover': { bgcolor: theme.palette.primary.dark },
                  }}
                  size="small"
                  onClick={() => {
                    if (closeDrawerOnMobile) closeDrawerOnMobile();
                    if (toggleBalanceDrawer) toggleBalanceDrawer();
                  }}
                >
                  <Add fontSize="small" />
                </IconButton>
              </Box>
            )}
            <Box display="flex" flexDirection="column" gap="0.5rem">
              <Button
                variant="contained"
                fullWidth
                text="Haz un envío"
                onClick={() => {
                  if (closeDrawerOnMobile) closeDrawerOnMobile();
                  navigate('/');
                }}
              />
              <Button
                variant="outlined"
                fullWidth
                text="Carga masiva de envíos"
                onClick={() => {
                  if (closeDrawerOnMobile) closeDrawerOnMobile();
                  const token = localStorage.getItem('token');
                  const url = token
                    ? `${VITE_MASSIVE_LOAD_URL}?token=${encodeURIComponent(token)}`
                    : VITE_MASSIVE_LOAD_URL;
                  window.open(url, '_blank');
                }}
              />
            </Box>
            <Divider sx={{ mt: '0.5rem' }} />
          </Box>
        )}
        <CollapsableItem title="Envíos" icon={<ShipmentsIcon />}>
          <ListItemButtonChildren
            isSelected={isActive('/')}
            isChildren
            onClick={closeDrawerOnMobile}
            isOpenSidebar={openDrawer}
            component={Link}
            to="/"
          >
            <LocalShippingOutlined />
            <ListItemTextCustom primary="Haz un envío" />
          </ListItemButtonChildren>

          {availableCoverage.includes(country!) && (
            <ListItemButtonChildren
              isSelected={isActive('/coverage')}
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              to="/coverage"
            >
              <Coverage />
              <ListItemTextCustom primary="Cotizador" />
            </ListItemButtonChildren>
          )}

          <ListItemButtonChildren
            isSelected={isActive('/pickups')}
            isChildren
            isOpenSidebar={openDrawer}
            component={Link}
            onClick={closeDrawerOnMobile}
            to="/pickups"
          >
            <Pickups />

            <ListItemTextCustom primary="Recolecciones" />
          </ListItemButtonChildren>

          <ListItemButtonChildren
            isSelected={isActive('/my-shipments')}
            isChildren
            isOpenSidebar={openDrawer}
            component={Link}
            onClick={closeDrawerOnMobile}
            to="/my-shipments"
          >
            <Shipments />

            <ListItemTextCustom primary="Mis envíos" />
          </ListItemButtonChildren>
        </CollapsableItem>
        {INTERNATIONAL_SECTION_ENABLED &&
          country === 'MEX' &&
          profile.data?.configuration.paymentType === 'PREPAID' && (
            <CollapsableItem title="Envío Internacional" icon={<InternationalIcon />}>
              <ListItemButtonChildren
                isSelected={isActive('/create-international-order')}
                isChildren
                onClick={closeDrawerOnMobile}
                isOpenSidebar={openDrawer}
                component={Link}
                to="/create-international-order"
              >
                <LocalShippingOutlined />
                <ListItemTextCustom primary="Haz un envío" />
              </ListItemButtonChildren>

              <ListItemButtonChildren
                isSelected={isActive('/ups-access-point')}
                isChildren
                isOpenSidebar={openDrawer}
                component={Link}
                onClick={closeDrawerOnMobile}
                to="/ups-access-point"
              >
                <AboutP99 />
                <ListItemTextCustom primary="Puntos UPS cercanos" />
              </ListItemButtonChildren>

              <ListItemButtonChildren
                isSelected={isActive('/international-history')}
                isChildren
                isOpenSidebar={openDrawer}
                component={Link}
                onClick={closeDrawerOnMobile}
                to="/international-history"
              >
                <HistoryIcon />
                <ListItemTextCustom primary="Pedidos en curso" />
              </ListItemButtonChildren>

              <ListItemButtonChildren
                isSelected={isActive('/international-support')}
                isChildren
                isOpenSidebar={openDrawer}
                component={Link}
                onClick={closeDrawerOnMobile}
                to="/international-support"
              >
                <Support />

                <ListItemTextCustom primary="FAQs" />
              </ListItemButtonChildren>
            </CollapsableItem>
          )}

        <CollapsableItem title="Administración" icon={<Management />}>
          <ListItemButtonChildren
            isSelected={isActive('/reports')}
            isChildren
            isOpenSidebar={openDrawer}
            component={Link}
            onClick={closeDrawerOnMobile}
            to="/reports"
          >
            <Reports />

            <ListItemTextCustom primary="Mis reportes" />
          </ListItemButtonChildren>

          {country === 'MEX' && (
            <ListItemButtonChildren
              isSelected={isActive('/invoices')}
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              to="/invoices"
            >
              <Invoices />

              <ListItemTextCustom primary="Mis facturas" />
            </ListItemButtonChildren>
          )}
          {role === 'master' && (
            <ListItemButtonChildren
              isSelected={isActive('/users')}
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              to="/users"
            >
              <MyUsers />

              <ListItemTextCustom primary="Mis usuarios" />
            </ListItemButtonChildren>
          )}
          {availableRates.includes(country!) && (
            <ListItemButtonChildren
              isSelected={isActive('/rates')}
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              to="/rates"
            >
              <Rates />

              <ListItemTextCustom primary="Mis tarifas" />
            </ListItemButtonChildren>
          )}
        </CollapsableItem>

        {hasFulfillAccess && (
          <CollapsableItem title="Fulfillment99" icon={<FulfillIcon />}>
            <ListItemButtonChildren
              isChildren
              isOpenSidebar={openDrawer}
              isSelected={isActive('/inventory')}
              component={Link}
              onClick={closeDrawerOnMobile}
              to="/inventory"
            >
              <Inventory />

              <ListItemTextCustom primary="Inventario" />
            </ListItemButtonChildren>

            <ListItemButtonChildren
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              isSelected={isActive('/inventory-history')}
              to="/inventory-history"
            >
              <InventoryHistory />

              <ListItemTextCustom primary="Órdenes de arribo" />
            </ListItemButtonChildren>

            <ListItemButtonChildren
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              isSelected={isActive('/order-status')}
              to="/order-status"
            >
              <OrderStatusFF />

              <ListItemTextCustom primary="Estatus de órdenes" />
            </ListItemButtonChildren>

            <ListItemButtonChildren
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              isSelected={isActive('/batches')}
              to="/batches"
            >
              <BatchesIcon />

              <ListItemTextCustom primary="Lotes" />
            </ListItemButtonChildren>

            <ListItemButtonChildren
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              isSelected={isActive('/warehouses')}
              to="/warehouses"
            >
              <WarehouseOutlined />

              <ListItemTextCustom primary="Almacenes" />
            </ListItemButtonChildren>
          </CollapsableItem>
        )}

        {/* Sección de Plugins */}
        <CollapsableItem title="Plugins" icon={<ExtensionOutlined />}>
          <ListItemButtonChildren
            isSelected={isActive('/my-plugins')}
            isChildren
            isOpenSidebar={openDrawer}
            component={Link}
            onClick={closeDrawerOnMobile}
            to="/my-plugins"
          >
            <AppsOutlined />

            <ListItemTextCustom primary="Mis plugins" />
          </ListItemButtonChildren>

          {hasEnvioFlexAccess && (
            <ListItemButtonChildren
              isSelected={isActive('/envio-flex')}
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              to="/envio-flex"
            >
              <LocalShippingOutlined />

              <ListItemTextCustom primary="Mercado Libre Flex" />
            </ListItemButtonChildren>
          )}

          {hasMLFulfillAccess && (
            <ListItemButtonChildren
              isSelected={isActive('/ml-fulfill')}
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              to="/ml-fulfill"
            >
              <LocalShippingOutlined />

              <ListItemTextCustom primary="Mercado Libre Fulfill99" />
            </ListItemButtonChildren>
          )}

          {hasMLParcelAccess && (
            <ListItemButtonChildren
              isSelected={isActive('/ml-parcel')}
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              to="/ml-parcel"
            >
              <LocalShippingOutlined />
              <ListItemTextCustom primary="Mercado Libre Parcel" />
            </ListItemButtonChildren>
          )}

          {isFalabellaEnabledCountry(country) && hasFalabellaAccess && (
            <ListItemButtonChildren
              isSelected={isActive('/falabella')}
              isChildren
              isOpenSidebar={openDrawer}
              component={Link}
              onClick={closeDrawerOnMobile}
              to="/falabella"
            >
              <LocalShippingOutlined />
              <ListItemTextCustom primary="Falabella" />
            </ListItemButtonChildren>
          )}
        </CollapsableItem>

        {country === 'MEX' && (
          <ListItemButtonChildren
            isSelected={isActive('/statistics')}
            component={Link}
            onClick={closeDrawerOnMobile}
            to="/statistics"
          >
            <Dashboard />

            <ListItemTextCustom primary="Estadísticas" />
          </ListItemButtonChildren>
        )}

        <Divider sx={{ my: '0.5rem' }} />

        <ListItemButtonChildren
          component="a"
          href={SIDEBAR_PATHS.integrations}
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeDrawerOnMobile}
          aria-label="Integraciones (abre en nueva ventana)"
        >
          <Integrations />
          <ListItemTextCustom primary="Integraciones" />
          <ExternalLinkIcon openDrawer={openDrawer} />
        </ListItemButtonChildren>

        {country === 'MEX' && (
          <CollapsableItem title="punto99" icon={<P99 />}>
            <ListItemButtonChildren
              isChildren
              isOpenSidebar={openDrawer}
              component="a"
              href={SIDEBAR_PATHS.p99.locations}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeDrawerOnMobile}
              aria-label="Ubicaciones (abre en nueva ventana)"
            >
              <PointLocation />
              <ListItemTextCustom primary="Ubicaciones" />
              <ExternalLinkIcon openDrawer={openDrawer} />
            </ListItemButtonChildren>

            <ListItemButtonChildren
              isChildren
              isOpenSidebar={openDrawer}
              component="a"
              href={SIDEBAR_PATHS.p99.aboutP99}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeDrawerOnMobile}
              aria-label="Sobre punto99 (abre en nueva ventana)"
            >
              <AboutP99 />
              <ListItemTextCustom primary="Sobre punto99" />
              <ExternalLinkIcon openDrawer={openDrawer} />
            </ListItemButtonChildren>
          </CollapsableItem>
        )}

        <ListItemButtonChildren
          isSelected={isActive('/resources')}
          component={Link}
          onClick={closeDrawerOnMobile}
          to="/resources"
        >
          <Resources />

          <ListItemTextCustom primary="Recursos" />
        </ListItemButtonChildren>
      </List>
    </Box>
  );
};

export default DrawerMenu;
